import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  Target, TrendingUp, ChevronRight, ChevronLeft, Zap, IndianRupee,
  Briefcase, BookOpen, Rocket, AlertTriangle, Check, Clock, Award,
  ArrowRight, Sparkles, BarChart3, Users, GraduationCap, Star,
  Lightbulb, MapPin, ExternalLink, Play, Lock, Send,
  Share2, Download, Bookmark, Mail, Link2, Copy, ImageIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Constants ────────────────────────────────────────────────── */
const EDUCATION_OPTIONS = [
  { value: '10th Pass', label: '10th Pass', icon: '📝' },
  { value: 'Inter / 12th', label: 'Inter / 12th', icon: '📘' },
  { value: 'Diploma', label: 'Diploma', icon: '🎓' },
  { value: 'B.Tech / B.E.', label: 'B.Tech / B.E.', icon: '⚙' },
  { value: 'B.Sc / B.Com / BA', label: 'B.Sc / B.Com / BA', icon: '📊' },
  { value: 'BBA / BBM', label: 'BBA / BBM', icon: '💼' },
  { value: 'MBA', label: 'MBA', icon: '🏢' },
  { value: 'MCA / M.Tech', label: 'MCA / M.Tech', icon: '💻' },
  { value: 'Non-IT Background', label: 'Non-IT Background', icon: '🔧' },
  { value: 'Dropout', label: 'Dropout', icon: '🔄' },
];

const SKILL_OPTIONS = [
  'Python', 'JavaScript', 'React', 'Java', 'SQL', 'Excel',
  'HTML/CSS', 'Digital Marketing', 'SEO', 'Social Media',
  'Content Writing', 'Video Editing', 'Graphic Design', 'UI/UX',
  'Data Analysis', 'Machine Learning', 'Cloud (AWS/Azure)', 'Sales',
  'Public Speaking', 'English Communication', 'Tally / Accounting',
  'Photography', 'Mobile App Dev', 'WordPress', 'Canva',
];

const INCOME_OPTIONS = [
  { value: '₹10,000/month', label: '₹10K/mo', sub: 'Entry level' },
  { value: '₹25,000/month', label: '₹25K/mo', sub: 'Junior level' },
  { value: '₹50,000/month', label: '₹50K/mo', sub: 'Mid level' },
  { value: '₹1,00,000/month', label: '₹1L/mo', sub: 'Senior level' },
  { value: '₹2,50,000/month', label: '₹2.5L/mo', sub: 'Expert level' },
  { value: '₹5,00,000/month', label: '₹5L/mo', sub: 'Leadership' },
  { value: '₹10,00,000/month', label: '₹10L/mo', sub: 'C-Suite / Founder' },
  { value: '₹1,00,00,000/month', label: '₹1Cr+/mo', sub: 'Visionary' },
];

const TIMELINE_OPTIONS = [
  { value: '0-3 months', label: '0-3 Months', desc: 'Urgent' },
  { value: '3-6 months', label: '3-6 Months', desc: 'Short-term' },
  { value: '6-12 months', label: '6-12 Months', desc: 'Medium' },
  { value: '1-3 years', label: '1-3 Years', desc: 'Long-term' },
];

const WORK_OPTIONS = [
  { value: 'Job', label: 'Full-time Job', icon: Briefcase, desc: 'Stable salary' },
  { value: 'Freelancing', label: 'Freelancing', icon: Rocket, desc: 'Flexibility' },
  { value: 'Business', label: 'Start Business', icon: TrendingUp, desc: 'Own venture' },
  { value: 'Open to all', label: 'Open to All', icon: Sparkles, desc: 'Best option' },
];

const LEVEL_CONFIG = {
  0: { color: 'bg-red-500', label: 'Level 0', tag: 'No Skills', width: '5%' },
  1: { color: 'bg-orange-500', label: 'Level 1', tag: 'Basic Skills', width: '25%' },
  2: { color: 'bg-yellow-500', label: 'Level 2', tag: 'Partial Skills', width: '50%' },
  3: { color: 'bg-blue-500', label: 'Level 3', tag: 'Job-Ready', width: '75%' },
  4: { color: 'bg-green-500', label: 'Level 4', tag: 'High-Income', width: '100%' },
};

/* ─── Lead Gate Constants ──────────────────────────────────────── */
const GATE_EDUCATION_OPTIONS = [
  '10th Class', '11th/12th (Intermediate)', 'Diploma', 'B.Tech/B.E.', 'B.Sc', 'B.Com', 'BBA/BBM',
  'B.Pharm', 'MBBS/BDS', 'M.Tech/M.E.', 'MBA', 'M.Sc', 'Other'
];
const GATE_FUTURE_OPTIONS = [
  'Get a Job', 'Higher Studies in India', 'Study Abroad', 'Start a Business',
  'Prepare for Government Exams', 'Freelancing / Remote Work', 'Not Sure Yet'
];

/* ─── Action Task Item (interactive checkbox) ──────────────────── */
const ActionTaskItem = ({ item, blockIdx, taskIdx, blockLabelKey, badgeClass }) => {
  const storageKey = `s2i-action-${blockIdx}-${taskIdx}`;
  const [checked, setChecked] = useState(() => localStorage.getItem(storageKey) === 'true');
  const toggleCheck = () => {
    const next = !checked;
    setChecked(next);
    localStorage.setItem(storageKey, String(next));
  };
  return (
    <div
      onClick={toggleCheck}
      className={`bg-white rounded-lg p-3.5 border cursor-pointer transition-all ${checked ? 'border-green-300 bg-green-50/50' : 'border-earth-200 hover:border-earth-300 hover:shadow-sm'}`}
    >
      <div className="flex gap-3">
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${checked ? 'bg-green-500 border-green-500' : 'border-earth-300'}`}>
          {checked && <Check className="w-3 h-3 text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${badgeClass} px-1.5 py-0.5 rounded`}>
              {blockLabelKey === 'day' ? `Day ${item.day || item.week}` : `Week ${item.week || item.day}`}
            </span>
          </div>
          <p className={`text-sm font-semibold leading-snug ${checked ? 'text-earth-400 line-through' : 'text-earth-700'}`}>{item.action}</p>
          <div className="flex items-start gap-1.5 mt-1.5">
            <ArrowRight className={`w-3 h-3 flex-shrink-0 mt-0.5 ${checked ? 'text-green-400' : 'text-earth-400'}`} />
            <p className={`text-xs leading-relaxed ${checked ? 'text-earth-400' : 'text-earth-500'}`}><span className="font-medium">Result:</span> {item.outcome}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Inline Lead Gate Form ──────────────────────────────────────── */
const S2ILeadGateForm = ({ onSuccess }) => {
  const [gateStep, setGateStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', city: '', age: '',
    education_level: '', college_name: '', year_of_study: '',
    future_plan: '', dream_career: '', preferred_city: ''
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.email || !form.education_level || !form.future_plan) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: 'skill2income_gate' });
      localStorage.setItem('o4s_lead_submitted', 'true');
      toast.success('Unlocked! Scroll down for your full income roadmap.');
      onSuccess();
    } catch (e) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="s2i-lead-gate-section" className="relative my-12">
      <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-lg z-10">
        <Lock className="w-5 h-5 text-white" />
      </div>
      <div className="bg-white rounded-2xl shadow-xl border-2 border-primary/20 overflow-hidden">
        <div className="bg-secondary text-white px-6 py-6 text-center">
          <h3 className="text-xl font-heading font-bold mb-1">Unlock Your Complete Income Roadmap</h3>
          <p className="text-white/70 text-sm">Fill in your details to see earning paths, action plan & course recommendations</p>
          <div className="flex gap-1.5 mt-4 justify-center max-w-xs mx-auto">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex-1 h-1.5 rounded-full transition-all ${gateStep >= s ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>
        <div className="p-6 md:p-8 max-w-lg mx-auto space-y-5">
          {gateStep === 1 && (
            <div data-testid="s2i-gate-step-1" className="space-y-4 animate-fade-in">
              <h4 className="text-lg font-bold text-earth-700">Your Details</h4>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Full Name *</label>
                <Input data-testid="s2i-gate-name" placeholder="Enter your full name" value={form.name}
                  onChange={e => update('name', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-earth-600 mb-1 block">Phone *</label>
                  <Input data-testid="s2i-gate-phone" placeholder="10-digit number" value={form.phone}
                    onChange={e => update('phone', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" type="tel" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-earth-600 mb-1 block">City *</label>
                  <Input data-testid="s2i-gate-city" placeholder="Hyderabad" value={form.city}
                    onChange={e => update('city', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Email *</label>
                <Input data-testid="s2i-gate-email" placeholder="your@email.com" value={form.email}
                  onChange={e => update('email', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" type="email" />
              </div>
              <Button data-testid="s2i-gate-next-1" className="w-full bg-primary hover:bg-primary-hover text-white h-12 rounded-xl btn-press"
                onClick={() => { if (!form.name || !form.phone || !form.email || !form.city) { toast.error('Please fill all required fields'); return; } setGateStep(2); }}>
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
          {gateStep === 2 && (
            <div data-testid="s2i-gate-step-2" className="space-y-4 animate-fade-in">
              <h4 className="text-lg font-bold text-earth-700">Current Education</h4>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-2 block">Education Level *</label>
                <div className="grid grid-cols-2 gap-2">
                  {GATE_EDUCATION_OPTIONS.map(opt => (
                    <button key={opt} data-testid={`s2i-gate-edu-${opt.replace(/[^a-zA-Z]/g, '')}`}
                      onClick={() => update('education_level', opt)}
                      className={`text-left px-3 py-2.5 rounded-xl text-sm border transition-all ${form.education_level === opt ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-earth-200 text-earth-600 hover:border-earth-300'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">College / School</label>
                <Input data-testid="s2i-gate-college" placeholder="Name (optional)" value={form.college_name}
                  onChange={e => update('college_name', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setGateStep(1)} variant="outline" className="flex-1 h-12 rounded-xl border-earth-200 text-earth-600">Back</Button>
                <Button data-testid="s2i-gate-next-2" className="flex-1 bg-primary hover:bg-primary-hover text-white h-12 rounded-xl btn-press"
                  onClick={() => { if (!form.education_level) { toast.error('Please select education level'); return; } setGateStep(3); }}>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
          {gateStep === 3 && (
            <div data-testid="s2i-gate-step-3" className="space-y-4 animate-fade-in">
              <h4 className="text-lg font-bold text-earth-700">Future Plans</h4>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-2 block">What next? *</label>
                <div className="grid grid-cols-1 gap-2">
                  {GATE_FUTURE_OPTIONS.map(opt => (
                    <button key={opt} data-testid={`s2i-gate-plan-${opt.replace(/[^a-zA-Z]/g, '')}`}
                      onClick={() => update('future_plan', opt)}
                      className={`text-left px-4 py-3 rounded-xl text-sm border transition-all ${form.future_plan === opt ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-earth-200 text-earth-600 hover:border-earth-300'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Dream Career</label>
                <Input data-testid="s2i-gate-dream" placeholder="e.g., Software Developer" value={form.dream_career}
                  onChange={e => update('dream_career', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setGateStep(2)} variant="outline" className="flex-1 h-12 rounded-xl border-earth-200 text-earth-600">Back</Button>
                <Button data-testid="s2i-gate-submit" onClick={handleSubmit} disabled={loading}
                  className="flex-1 bg-primary hover:bg-primary-hover text-white h-12 rounded-xl btn-press">
                  {loading ? 'Unlocking...' : 'Unlock Full Roadmap'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Multi-Step Form ──────────────────────────────────────────── */
const Skill2IncomePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem('o4s_lead_submitted') === 'true');
  const [shareNameModal, setShareNameModal] = useState(false);
  const [shareName, setShareName] = useState('');
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadMsgIndex, setLoadMsgIndex] = useState(0);
  const progressTimerRef = useRef(null);
  const msgTimerRef = useRef(null);
  const resultRef = useRef(null);
  const [searchParams] = useSearchParams();

  // Load existing analysis from URL query param (from dashboard history)
  useEffect(() => {
    const analysisId = searchParams.get('id');
    if (analysisId) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/skill2income/${analysisId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      }).then(r => {
        if (r.data.status === 'done' && r.data.result) {
          setResult(r.data.result);
          setAnalysisId(analysisId);
          setStep(6);
        }
      }).catch(() => {});
    }
  }, [searchParams]);

  const [form, setForm] = useState({
    education: '',
    current_skills: [],
    custom_skills: '',
    target_income: '',
    timeline: '',
    work_preference: '',
  });

  const toggleSkill = (skill) => {
    setForm(prev => ({
      ...prev,
      current_skills: prev.current_skills.includes(skill)
        ? prev.current_skills.filter(s => s !== skill)
        : [...prev.current_skills, skill]
    }));
  };

  const getLoadingMessages = () => {
    const workPref = form.work_preference || 'Job';
    const target = form.target_income || '';
    const base = [
      'Reading your profile...',
      'Evaluating your skill set...',
      'Mapping income opportunities...',
      'Analyzing market demand...',
      'Building your income roadmap...',
      'Calculating earning potential...',
      'Crafting 3 earning paths...',
      'Generating 90-day action plan...',
      'Finding best courses for you...',
      'Polishing your personalized report...',
    ];
    if (workPref === 'Freelancing') base[6] = 'Scouting freelance opportunities...';
    if (workPref === 'Business') base[6] = 'Evaluating business models...';
    return base;
  };

  const getMotivationalQuotes = () => {
    const workPref = form.work_preference || 'Job';
    const shared = [
      "Every expert was once a beginner.",
      "Skills pay the bills — and open doors.",
      "Your future self will thank you for starting today.",
      "The best investment is in yourself.",
    ];
    if (workPref === 'Freelancing') return [...shared,
      "Freedom is earned one skill at a time.",
      "Your skills are your startup capital.",
      "Clients don't hire degrees — they hire results.",
    ];
    if (workPref === 'Business') return [...shared,
      "Every big company started with one brave decision.",
      "Entrepreneurship: where problems become profits.",
      "Build something people need, and income follows.",
    ];
    return [...shared,
      "The right skills at the right time change everything.",
      "A great career isn't found — it's built.",
      "Consistency beats talent when talent doesn't show up.",
    ];
  };

  const startProgressAnimation = () => {
    setLoadProgress(0);
    setLoadMsgIndex(0);
    let p = 0;
    const messages = getLoadingMessages();
    // Smooth progress: 0→90% over ~25 seconds, last 10% on completion
    progressTimerRef.current = setInterval(() => {
      p += (90 - p) * 0.06 + 0.3;
      if (p > 90) p = 90;
      setLoadProgress(Math.round(p));
    }, 500);
    // Rotate messages every 2.5s
    let mi = 0;
    msgTimerRef.current = setInterval(() => {
      mi = (mi + 1) % messages.length;
      setLoadMsgIndex(mi);
    }, 2500);
  };

  const stopProgressAnimation = () => {
    clearInterval(progressTimerRef.current);
    clearInterval(msgTimerRef.current);
    setLoadProgress(100);
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login first to use Skill2Income');
      navigate('/auth');
      return;
    }
    setLoading(true);
    startProgressAnimation();
    try {
      const res = await axios.post(`${API}/skill2income`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const jobId = res.data.id;
      let pollCount = 0;
      const MAX_POLLS = 40; // ~2 minutes (40 x 3s)
      const poll = async () => {
        pollCount++;
        const r = await axios.get(`${API}/skill2income/${jobId}`);
        if (r.data.status === 'done') {
          stopProgressAnimation();
          setTimeout(() => {
            setResult(r.data.result);
            setAnalysisId(jobId);
            setLoading(false);
            setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 300);
          }, 600);
        } else if (r.data.status === 'error') {
          stopProgressAnimation();
          toast.error(r.data.error || 'Analysis failed. Please try again.');
          setLoading(false);
        } else if (pollCount >= MAX_POLLS) {
          stopProgressAnimation();
          toast.error('Analysis is taking too long. Please try again.');
          setLoading(false);
        } else {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      stopProgressAnimation();
      toast.error(e.response?.data?.detail || 'Failed to start analysis.');
      setLoading(false);
    }
  };

  const TOTAL_STEPS = 5;
  const canProceed = () => {
    if (step === 1) return !!form.education;
    if (step === 2) return true; // skills optional
    if (step === 3) return !!form.target_income;
    if (step === 4) return !!form.timeline;
    if (step === 5) return !!form.work_preference;
    return false;
  };

  /* ─── RESULTS VIEW ────────────────────────────────────────── */
  if (result) {
    const lvl = LEVEL_CONFIG[result.user_level?.level] || LEVEL_CONFIG[0];
    const shareUrl = window.location.href;
    const shareTitle = `My Career Roadmap — O4S.ai`;
    const shareText = `I just explored my income potential with O4S.ai — a free AI career guidance tool. Check yours too:`;

    const handleShare = (platform) => {
      const encodedUrl = encodeURIComponent(shareUrl);
      const encodedText = encodeURIComponent(shareText);
      const encodedTitle = encodeURIComponent(shareTitle);
      const waMsg = `${shareTitle}\n\n${shareText}\n${shareUrl}`;
      const urls = {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(waMsg)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
        gmail: `https://mail.google.com/mail/?view=cm&su=${encodedTitle}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
        telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      };
      window.open(urls[platform], '_blank', 'noopener,noreferrer');
    };

    const handleCopyLink = () => {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    };

    const handleBookmark = () => {
      toast.info('Press Ctrl+D (Cmd+D on Mac) to bookmark this page!');
    };

    const handleDownloadPDF = async () => {
      if (!analysisId) { toast.error('Analysis ID not found'); return; }
      try {
        toast.info('Generating your PDF...');
        const response = await axios.get(`${API}/skill2income/${analysisId}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `skill2income-roadmap-${analysisId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded!');
      } catch {
        toast.error('Failed to generate PDF. Please try again.');
      }
    };

    const handleShareImage = () => {
      if (!analysisId) { toast.error('Analysis ID not found'); return; }
      setShareNameModal(true);
    };

    const generateAndShareImage = async () => {
      setShareNameModal(false);
      try {
        toast.info('Generating your personalized card...');
        const params = shareName.trim() ? `?name=${encodeURIComponent(shareName.trim())}` : '';
        const response = await axios.get(`${API}/skill2income/${analysisId}/share-image${params}`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'image/png' });
        const file = new File([blob], `skill2income-card-${analysisId}.png`, { type: 'image/png' });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ title: shareTitle, text: shareText, files: [file] });
          toast.success('Shared successfully!');
        } else {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `skill2income-card-${analysisId}.png`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success('Image downloaded! Share it on your favorite platform.');
        }
      } catch {
        toast.error('Failed to generate share image.');
      }
    };

    return (
      <div className="min-h-screen bg-background" ref={resultRef}>
        <SEOHead title="Your Income Roadmap — Skill2Income | O4S.ai" path="/skill2income" />

        {/* Nav */}
        <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-3 border-b border-earth-200">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-earth-700">Skill2Income</h1>
                <p className="text-xs text-earth-500">Your Personalized Income Roadmap</p>
              </div>
            </div>
            <Button data-testid="s2i-restart-btn" onClick={() => { setResult(null); setStep(1); }} variant="outline" className="border-earth-200 text-earth-600 rounded-xl">
              New Analysis
            </Button>
          </div>
        </nav>

        {/* Share & Actions Bar */}
        <div className="px-4 md:px-8 py-3 bg-surface-alt border-b border-earth-200">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-earth-500 uppercase tracking-wider mr-1 flex items-center gap-1">
                <Share2 className="w-3.5 h-3.5" /> Share
              </span>
              <button data-testid="s2i-share-whatsapp" onClick={() => handleShare('whatsapp')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-earth-200 bg-white text-earth-600 hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all">
                <svg className="w-3.5 h-3.5" fill="#25D366" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                WhatsApp
              </button>

              {/* LinkedIn */}
              <button data-testid="s2i-share-linkedin" onClick={() => handleShare('linkedin')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-earth-200 bg-white text-earth-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all">
                <svg className="w-3.5 h-3.5" fill="#0A66C2" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                LinkedIn
              </button>

              {/* Facebook */}
              <button data-testid="s2i-share-facebook" onClick={() => handleShare('facebook')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-earth-200 bg-white text-earth-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all">
                <svg className="w-3.5 h-3.5" fill="#1877F2" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </button>

              {/* Twitter/X */}
              <button data-testid="s2i-share-twitter" onClick={() => handleShare('twitter')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-earth-200 bg-white text-earth-600 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-all">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X
              </button>

              {/* Gmail */}
              <button data-testid="s2i-share-gmail" onClick={() => handleShare('gmail')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-earth-200 bg-white text-earth-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all">
                <Mail className="w-3.5 h-3.5" />
                Gmail
              </button>

              {/* Telegram */}
              <button data-testid="s2i-share-telegram" onClick={() => handleShare('telegram')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-earth-200 bg-white text-earth-600 hover:bg-sky-50 hover:border-sky-300 hover:text-sky-600 transition-all">
                <Send className="w-3.5 h-3.5" />
                Telegram
              </button>

              <div className="w-px h-5 bg-earth-200 mx-1 hidden sm:block"></div>

              {/* Copy Link */}
              <button data-testid="s2i-copy-link" onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-earth-200 bg-white text-earth-600 hover:bg-earth-50 hover:border-earth-300 transition-all">
                <Copy className="w-3.5 h-3.5" />
                Copy Link
              </button>

              {/* Bookmark */}
              <button data-testid="s2i-bookmark" onClick={handleBookmark}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-earth-200 bg-white text-earth-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-all">
                <Bookmark className="w-3.5 h-3.5" />
                Bookmark
              </button>

              {/* Share as Image */}
              <button data-testid="s2i-share-image" onClick={handleShareImage}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/50 transition-all">
                <ImageIcon className="w-3.5 h-3.5" />
                Share as Image
              </button>

              {/* Download PDF */}
              <button data-testid="s2i-download-pdf" onClick={handleDownloadPDF}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary-hover transition-all ml-auto">
                <Download className="w-3.5 h-3.5" />
                Download PDF
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-8">
          {/* 1. USER LEVEL — Always visible */}
          <section data-testid="s2i-level-section" className="bg-white rounded-2xl border border-earth-200 p-6 md:p-8">
            <h2 className="text-xl font-bold text-earth-700 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" /> Your Current Level</h2>
            <div className="flex items-center gap-4 mb-4">
              <div className={`${lvl.color} text-white px-4 py-2 rounded-xl text-lg font-bold`}>{lvl.label}</div>
              <span className="text-lg font-semibold text-earth-700">{lvl.tag}</span>
              <span className="ml-auto text-sm font-semibold text-primary">{result.user_level?.current_earning_potential}</span>
            </div>
            <div className="w-full bg-earth-100 rounded-full h-3 mb-3">
              <div className={`${lvl.color} h-3 rounded-full transition-all duration-1000`} style={{ width: lvl.width }}></div>
            </div>
            <p className="text-earth-600">{result.user_level?.description}</p>
          </section>

          {/* 2. REALITY CHECK — Always visible */}
          <section data-testid="s2i-reality-section" className="bg-white rounded-2xl border-2 border-yellow-200 p-6 md:p-8">
            <h2 className="text-xl font-bold text-earth-700 mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-yellow-600" /> Reality Check</h2>
            <div className={`rounded-xl p-4 mb-4 ${result.reality_check?.is_realistic ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <p className={`font-semibold ${result.reality_check?.is_realistic ? 'text-green-700' : 'text-yellow-700'}`}>
                {result.reality_check?.is_realistic ? 'Your goal is achievable!' : 'Reality Gap Detected'}
              </p>
              <p className="text-earth-600 mt-1">{result.reality_check?.gap_message}</p>
            </div>
            <p className="text-sm text-earth-500 mb-4">Adjusted Timeline: <span className="font-bold text-earth-700">{result.reality_check?.adjusted_timeline}</span></p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {result.reality_check?.milestones?.map((m, i) => (
                <div key={i} className="bg-earth-50 rounded-xl p-4 border border-earth-200">
                  <p className="text-xs uppercase font-bold text-primary tracking-wider">{m.month} months</p>
                  <p className="text-lg font-bold text-earth-700 mt-1">{m.target}</p>
                  <p className="text-xs text-earth-500 mt-1">{m.what}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 3. RECOMMENDED SKILLS — Always visible */}
          <section data-testid="s2i-skills-section" className="bg-white rounded-2xl border border-earth-200 p-6 md:p-8">
            <h2 className="text-xl font-bold text-earth-700 mb-4 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-accent" /> Recommended Skills to Learn</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {result.recommended_skills?.map((skill, i) => (
                <div key={i} className="bg-surface-alt rounded-xl p-4 border border-earth-200 hover:border-primary transition-all">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-earth-700">{skill.skill}</h4>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${skill.demand_level === 'Extremely High' ? 'bg-red-100 text-red-700' : skill.demand_level === 'Very High' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                      {skill.demand_level}
                    </span>
                  </div>
                  <p className="text-sm text-earth-600 mb-2">{skill.why}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-white px-2 py-1 rounded border border-earth-200 text-earth-600"><IndianRupee className="w-3 h-3 inline" /> {skill.income_potential}</span>
                    <span className="bg-white px-2 py-1 rounded border border-earth-200 text-earth-600"><Clock className="w-3 h-3 inline" /> {skill.learning_time}</span>
                    <span className="bg-white px-2 py-1 rounded border border-earth-200 text-earth-600">{skill.difficulty}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ─── LEAD GATE — shown between free and gated sections ──── */}
          {!unlocked && (
            <S2ILeadGateForm onSuccess={() => setUnlocked(true)} />
          )}

          {/* ═══ GATED SECTIONS (blurred if locked) ═══════════════ */}

          {/* 4. INCOME ROADMAP — Gated */}
          <section data-testid="s2i-roadmap-section" className={`bg-secondary rounded-2xl p-6 md:p-8 text-white relative ${!unlocked ? 'select-none' : ''}`}>
            {!unlocked && (
              <div className="absolute inset-0 z-10 rounded-2xl backdrop-blur-md bg-white/60 flex flex-col items-center justify-center gap-3">
                <Lock className="w-8 h-8 text-primary/60" />
                <p className="text-earth-600 font-semibold text-center px-4">Fill the form above to unlock your income roadmap</p>
              </div>
            )}
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2"><MapPin className="w-5 h-5" /> Your Income Roadmap</h2>
            <div className="space-y-4">
              {result.income_roadmap?.phases?.map((phase, i) => (
                <div key={i} className="relative flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-white text-secondary flex items-center justify-center text-base font-extrabold shadow-lg flex-shrink-0">{phase.phase}</div>
                    {i < (result.income_roadmap?.phases?.length || 0) - 1 && <div className="w-0.5 flex-1 bg-white/30 my-1"></div>}
                  </div>
                  <div className="flex-1 bg-white rounded-xl p-5 shadow-md mb-1">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div>
                        <h4 className="font-bold text-lg text-earth-700">{(phase.title && phase.title.trim()) || `Phase ${phase.phase}`}</h4>
                        <p className="text-earth-500 text-sm">{phase.timeline}</p>
                      </div>
                      <span className="bg-primary text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-sm whitespace-nowrap">{phase.income_range}</span>
                    </div>
                    <ul className="space-y-1.5 mt-3">
                      {phase.activities?.map((a, j) => (
                        <li key={j} className="text-sm text-earth-600 flex gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {a}</li>
                      ))}
                    </ul>
                    <div className="mt-3 pt-2 border-t border-earth-100">
                      <p className="text-xs text-earth-400 italic flex items-center gap-1"><Award className="w-3 h-3" /> {phase.key_milestone}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 5. EARNING PATHS — Gated */}
          <section data-testid="s2i-paths-section" className={`relative ${!unlocked ? 'select-none' : ''}`}>
            {!unlocked && (
              <div className="absolute inset-0 z-10 rounded-2xl backdrop-blur-md bg-white/60 flex flex-col items-center justify-center gap-3">
                <Lock className="w-8 h-8 text-primary/60" />
                <p className="text-earth-600 font-semibold text-center px-4">Fill the form above to unlock earning paths</p>
              </div>
            )}
            <h2 className="text-xl font-bold text-earth-700 mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5 text-primary" /> 3 Earning Paths</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {result.earning_paths?.map((path, i) => {
                const colors = ['border-blue-400 bg-blue-50', 'border-green-400 bg-green-50', 'border-purple-400 bg-purple-50'];
                const icons = [Briefcase, Rocket, TrendingUp];
                const Icon = icons[i] || Briefcase;
                return (
                  <div key={i} className={`rounded-2xl border-2 p-5 ${colors[i] || colors[0]}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-5 h-5" />
                      <h4 className="font-bold text-earth-700">{path.title}</h4>
                    </div>
                    <p className="text-2xl font-bold text-earth-700 mb-1">{path.monthly_income}</p>
                    <p className="text-xs text-earth-500 mb-3">Start in {path.timeline_to_start}</p>
                    <div className="space-y-2 mb-3">
                      {Array.isArray(path.steps) && path.steps.map((s, j) => (
                        <div key={j} className="flex gap-2 text-sm text-earth-600">
                          <span className="w-5 h-5 bg-white rounded-full flex items-center justify-center text-xs font-bold text-earth-500 flex-shrink-0">{j + 1}</span>
                          {s}
                        </div>
                      ))}
                    </div>
                    {Array.isArray(path.platforms) && path.platforms.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {path.platforms.map((p, j) => (
                          <span key={j} className="text-xs bg-white px-2 py-0.5 rounded font-medium text-earth-600">{p}</span>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                      <div>
                        <p className="font-semibold text-green-700 mb-1">Pros</p>
                        {Array.isArray(path.pros) && path.pros.map((p, j) => <p key={j} className="text-earth-600">+ {p}</p>)}
                      </div>
                      <div>
                        <p className="font-semibold text-red-700 mb-1">Cons</p>
                        {Array.isArray(path.cons) && path.cons.map((c, j) => <p key={j} className="text-earth-600">- {c}</p>)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* 6. 30-60-90 DAY ACTION PLAN — Gated */}
          <section data-testid="s2i-action-section" className={`bg-white rounded-2xl border border-earth-200 p-6 md:p-8 relative ${!unlocked ? 'select-none' : ''}`}>
            {!unlocked && (
              <div className="absolute inset-0 z-10 rounded-2xl backdrop-blur-md bg-white/60 flex flex-col items-center justify-center gap-3">
                <Lock className="w-8 h-8 text-primary/60" />
                <p className="text-earth-600 font-semibold text-center px-4">Fill the form above to unlock your action plan</p>
              </div>
            )}
            <h2 className="text-xl font-bold text-earth-700 mb-1 flex items-center gap-2"><Play className="w-5 h-5 text-primary" /> Your 90-Day Action Plan</h2>
            <p className="text-sm text-earth-500 mb-6">Follow this step-by-step plan to start earning. Check off tasks as you complete them!</p>
            
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { title: 'Month 1', subtitle: 'Foundation & Setup', emoji: '🚀', dayRange: 'Days 1-30', bg: 'bg-orange-50', border: 'border-orange-300', accent: 'text-orange-600', badge: 'bg-orange-100 text-orange-700', data: result.action_plan?.day_1_to_30, labelKey: 'day' },
                { title: 'Month 2', subtitle: 'Build & Practice', emoji: '⚡', dayRange: 'Days 31-60', bg: 'bg-blue-50', border: 'border-blue-300', accent: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', data: result.action_plan?.day_31_to_60, labelKey: 'week' },
                { title: 'Month 3', subtitle: 'Launch & Earn', emoji: '💰', dayRange: 'Days 61-90', bg: 'bg-green-50', border: 'border-green-300', accent: 'text-green-600', badge: 'bg-green-100 text-green-700', data: result.action_plan?.day_61_to_90, labelKey: 'week' },
              ].map((block, i) => (
                <div key={i} className={`${block.bg} rounded-xl border ${block.border} overflow-hidden`}>
                  {/* Phase Header */}
                  <div className="px-5 pt-5 pb-3">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-bold text-earth-700 text-lg">{block.title}</h4>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${block.badge} px-2 py-0.5 rounded-full`}>{block.dayRange}</span>
                    </div>
                    <p className={`text-xs font-medium ${block.accent}`}>{block.subtitle}</p>
                  </div>

                  {/* Tasks */}
                  <div className="px-4 pb-5 space-y-2.5">
                    {block.data?.map((item, j) => (
                      <ActionTaskItem key={j} item={item} blockIdx={i} taskIdx={j} blockLabelKey={block.labelKey} badgeClass={block.badge} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 7. COURSE RECOMMENDATIONS — Gated */}
          <section data-testid="s2i-courses-section" className={`bg-white rounded-2xl border border-earth-200 p-6 md:p-8 relative ${!unlocked ? 'select-none' : ''}`}>
            {!unlocked && (
              <div className="absolute inset-0 z-10 rounded-2xl backdrop-blur-md bg-white/60 flex flex-col items-center justify-center gap-3">
                <Lock className="w-8 h-8 text-primary/60" />
                <p className="text-earth-600 font-semibold text-center px-4">Fill the form above to unlock course recommendations</p>
              </div>
            )}
            <h2 className="text-xl font-bold text-earth-700 mb-2 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-primary" /> Recommended Courses & Programs</h2>
            <p className="text-sm text-earth-500 mb-5">Handpicked courses to help you reach your income goal faster</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {result.course_recommendations?.map((course, i) => (
                <div key={i} className="bg-surface-alt rounded-xl border border-earth-200 hover:border-primary hover:shadow-lg transition-all overflow-hidden flex flex-col">
                  {/* Course Header */}
                  <div className="p-5 pb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded">{course.provider}</span>
                      <span className="text-[10px] font-medium text-earth-400">Course {i + 1} of {result.course_recommendations.length}</span>
                    </div>
                    <h4 className="font-bold text-earth-700 text-base leading-snug mb-2">{course.title}</h4>
                    <p className="text-xs text-earth-500 leading-relaxed">{course.why_recommended}</p>
                  </div>

                  {/* Course Details */}
                  <div className="px-5 pb-4 flex-1">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-white rounded-lg p-2.5 border border-earth-100">
                        <p className="text-[10px] text-earth-400 font-medium uppercase tracking-wide mb-0.5">Duration</p>
                        <p className="text-sm font-semibold text-earth-700">{course.duration}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2.5 border border-earth-100">
                        <p className="text-[10px] text-earth-400 font-medium uppercase tracking-wide mb-0.5">Course Fee</p>
                        <p className="text-sm font-semibold text-earth-700">{course.cost}</p>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2.5 border border-green-200">
                      <p className="text-[10px] text-green-600 font-medium uppercase tracking-wide mb-0.5">Expected Income After Course</p>
                      <p className="text-sm font-bold text-green-700">{course.income_after_completion}</p>
                    </div>
                  </div>

                  {/* Course CTA */}
                  {(course.course_url || course.url_hint) && (
                    <div className="px-5 pb-4">
                      <a
                        href={course.course_url || (course.url_hint?.startsWith('http') ? course.url_hint : `https://${course.url_hint}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2.5 bg-primary/10 hover:bg-primary hover:text-white text-primary rounded-lg text-sm font-semibold transition-all"
                      >
                        <ExternalLink className="w-4 h-4" /> View Course on {course.provider}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* MOTIVATION — Gated */}
          {result.motivation && (
            <section className={`bg-gradient-to-r from-primary to-accent rounded-2xl p-6 md:p-8 text-center relative ${!unlocked ? 'select-none' : ''}`}>
              {!unlocked && (
                <div className="absolute inset-0 z-10 rounded-2xl backdrop-blur-md bg-white/60 flex flex-col items-center justify-center gap-3">
                  <Lock className="w-8 h-8 text-primary/60" />
                </div>
              )}
              <Sparkles className="w-8 h-8 text-white mx-auto mb-3" />
              <p className="text-white text-lg font-semibold max-w-2xl mx-auto leading-relaxed">{result.motivation}</p>
            </section>
          )}

          {/* MONETIZATION PLAN — Gated */}
          {result.monetization_plan && (
            <section data-testid="s2i-monetization-section" className={`bg-white rounded-2xl border border-earth-200 p-6 md:p-8 relative ${!unlocked ? 'select-none' : ''}`}>
              {!unlocked && (
                <div className="absolute inset-0 z-10 rounded-2xl backdrop-blur-md bg-white/60 flex flex-col items-center justify-center gap-3">
                  <Lock className="w-8 h-8 text-primary/60" />
                  <p className="text-earth-600 font-semibold text-center px-4">Fill the form above to unlock monetization plan</p>
                </div>
              )}
              <h2 className="text-xl font-bold text-earth-700 mb-2 flex items-center gap-2"><IndianRupee className="w-5 h-5 text-green-600" /> How to Start Earning</h2>
              <p className="text-earth-500 text-sm mb-4">{result.monetization_plan.strategy}</p>
              <p className="text-xs text-primary font-semibold mb-4">Expected first income: {result.monetization_plan.first_income_timeline}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {result.monetization_plan.steps?.map((s, i) => (
                  <div key={i} className="flex gap-3 bg-surface-alt rounded-xl p-4 border border-earth-200">
                    <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">{s.step}</div>
                    <div>
                      <p className="font-semibold text-earth-700 text-sm">{s.action}</p>
                      <p className="text-xs text-earth-500 mt-1">{s.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ─── FINAL CTA SECTION ─────────────────────────────── */}
          <section className="bg-white rounded-2xl border border-earth-200 p-6 md:p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-earth-700 mb-2">What's Next?</h2>
              <p className="text-earth-500">Your personalized income roadmap is ready. Take action now!</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <button onClick={() => navigate('/assessment')} className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-earth-200 hover:border-primary hover:bg-primary/5 transition-all group">
                <Zap className="w-8 h-8 text-earth-400 group-hover:text-primary transition-colors" />
                <span className="font-bold text-earth-700 text-sm">Career Assessment</span>
                <span className="text-xs text-earth-500">Get AI career recommendations</span>
              </button>
              <button onClick={() => navigate('/faqs')} className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-earth-200 hover:border-primary hover:bg-primary/5 transition-all group">
                <BookOpen className="w-8 h-8 text-earth-400 group-hover:text-primary transition-colors" />
                <span className="font-bold text-earth-700 text-sm">Ask O4S.AI</span>
                <span className="text-xs text-earth-500">Browse 1800+ career FAQs</span>
              </button>
              <button onClick={handleDownloadPDF} className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-earth-200 hover:border-primary hover:bg-primary/5 transition-all group">
                <Download className="w-8 h-8 text-earth-400 group-hover:text-primary transition-colors" />
                <span className="font-bold text-earth-700 text-sm">Download PDF</span>
                <span className="text-xs text-earth-500">Save your roadmap offline</span>
              </button>
            </div>
            <div className="text-center pt-4 border-t border-earth-200">
              <p className="text-sm text-earth-500 mb-3">Share this tool with friends who need career guidance</p>
              <div className="flex justify-center gap-2">
                <button onClick={() => handleShare('whatsapp')} className="w-10 h-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center hover:bg-green-100 transition-all">
                  <svg className="w-5 h-5" fill="#25D366" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                </button>
                <button onClick={() => handleShare('linkedin')} className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center hover:bg-blue-100 transition-all">
                  <svg className="w-5 h-5" fill="#0A66C2" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </button>
                <button onClick={() => handleShare('twitter')} className="w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </button>
                <button onClick={handleCopyLink} className="w-10 h-10 rounded-full bg-earth-50 border border-earth-200 flex items-center justify-center hover:bg-earth-100 transition-all">
                  <Link2 className="w-5 h-5 text-earth-500" />
                </button>
              </div>
              <p className="text-xs text-earth-400 mt-6">Powered by O4S.ai — AI Career Guidance for Indian Students</p>
            </div>
          </section>
        </div>

        {/* Share Image Name Modal */}
        {shareNameModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in" onClick={() => setShareNameModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="bg-secondary px-6 py-5 text-center">
                <ImageIcon className="w-8 h-8 text-white mx-auto mb-2" />
                <h3 className="text-lg font-bold text-white">Create Your Share Card</h3>
                <p className="text-white/70 text-sm">Add your name for a personalized card</p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-semibold text-earth-600 mb-1 block">Your Name</label>
                  <Input
                    data-testid="s2i-share-name-input"
                    placeholder="e.g., Rahul Sharma"
                    value={shareName}
                    onChange={e => setShareName(e.target.value)}
                    className="border-earth-200 focus:border-primary rounded-xl h-12 text-base"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') generateAndShareImage(); }}
                  />
                </div>
                <div className="flex gap-3">
                  <Button onClick={() => { setShareName(''); generateAndShareImage(); }} variant="outline" className="flex-1 h-11 rounded-xl border-earth-200 text-earth-500 text-sm">
                    Skip
                  </Button>
                  <Button data-testid="s2i-share-generate-btn" onClick={generateAndShareImage} className="flex-1 h-11 rounded-xl bg-primary hover:bg-primary-hover text-white text-sm font-semibold btn-press">
                    <ImageIcon className="w-4 h-4 mr-1.5" /> Generate Card
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─── INPUT FORM VIEW ──────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Skill2Income — Turn Skills into Income | O4S.ai" description="Discover exactly which skills to learn and how to earn ₹1 Lakh to ₹1 Crore per month. AI-powered income roadmap for Indian students and job seekers." path="/skill2income" />

      {/* Nav */}
      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-3 border-b border-earth-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-9 h-9 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-earth-700">Skill2Income</h1>
              <p className="text-xs text-earth-500">Income Recommendation Engine</p>
            </div>
          </div>
          <button onClick={() => navigate('/')} className="flex items-center text-earth-500 hover:text-primary text-sm font-medium transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to O4S
          </button>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Hero */}
        {step === 1 && !loading && (
          <div className="text-center mb-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">AI-Powered Income Engine</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-earth-700 mb-3">
              Know Your <span className="gradient-text">Earning Potential</span>
            </h2>
            <p className="text-earth-500 max-w-lg mx-auto">Get a personalized income roadmap with skill recommendations, earning paths, and a 90-day action plan.</p>
          </div>
        )}

        {/* Progress */}
        <div className="flex gap-1.5 mb-8 max-w-sm mx-auto">
          {[1, 2, 3, 4, 5].map(s => (
            <div key={s} className={`flex-1 h-2 rounded-full transition-all duration-300 ${step >= s ? 'bg-primary' : 'bg-earth-200'}`} />
          ))}
        </div>

        {/* Loading State */}
        {loading && (() => {
          const messages = getLoadingMessages();
          const quotes = getMotivationalQuotes();
          const currentMsg = messages[loadMsgIndex % messages.length];
          const currentQuote = quotes[Math.floor(loadMsgIndex / 2) % quotes.length];
          return (
            <div data-testid="s2i-loading-progress" className="py-12 animate-fade-in max-w-md mx-auto">
              {/* Circular icon */}
              <div className="relative w-24 h-24 mx-auto mb-8">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#E8E0D8" strokeWidth="6" />
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#D95A41" strokeWidth="6"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    strokeDashoffset={`${2 * Math.PI * 42 * (1 - loadProgress / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-500" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">{loadProgress}%</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-earth-100 rounded-full h-2.5 mb-6 overflow-hidden">
                <div className="h-2.5 rounded-full bg-gradient-to-r from-primary via-accent to-primary transition-all duration-500 ease-out"
                  style={{ width: `${loadProgress}%` }} />
              </div>

              {/* Dynamic message */}
              <div className="text-center mb-6">
                <p className="text-base font-semibold text-earth-700 transition-opacity duration-300" key={currentMsg}>
                  {currentMsg}
                </p>
                <p className="text-xs text-earth-400 mt-1">
                  {loadProgress < 30 ? 'Getting started...' : loadProgress < 60 ? 'Almost halfway...' : loadProgress < 90 ? 'Finishing up...' : 'Wrapping your report...'}
                </p>
              </div>

              {/* Motivational quote */}
              <div className="bg-surface-alt rounded-xl p-4 border border-earth-200 text-center">
                <Sparkles className="w-4 h-4 text-accent mx-auto mb-2" />
                <p className="text-sm text-earth-600 italic leading-relaxed transition-opacity duration-300" key={currentQuote}>
                  "{currentQuote}"
                </p>
              </div>

              <p className="text-earth-400 text-xs mt-6 text-center">This usually takes 15-30 seconds</p>
            </div>
          );
        })()}

        {/* STEP 1: Education */}
        {!loading && step === 1 && (
          <div data-testid="s2i-step-1" className="animate-fade-in">
            <h3 className="text-xl font-bold text-earth-700 mb-1">What's your education?</h3>
            <p className="text-sm text-earth-500 mb-5">Select the closest match</p>
            <div className="grid grid-cols-2 gap-3">
              {EDUCATION_OPTIONS.map(opt => (
                <button key={opt.value} data-testid={`s2i-edu-${opt.value.replace(/[^a-zA-Z]/g, '')}`}
                  onClick={() => setForm(p => ({ ...p, education: opt.value }))}
                  className={`text-left px-4 py-4 rounded-xl border-2 transition-all ${form.education === opt.value ? 'border-primary bg-primary/5 shadow-sm' : 'border-earth-200 hover:border-earth-300 bg-white'}`}>
                  <span className="text-lg mr-2">{opt.icon}</span>
                  <span className={`text-sm font-semibold ${form.education === opt.value ? 'text-primary' : 'text-earth-700'}`}>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2: Skills */}
        {!loading && step === 2 && (
          <div data-testid="s2i-step-2" className="animate-fade-in">
            <h3 className="text-xl font-bold text-earth-700 mb-1">What skills do you have?</h3>
            <p className="text-sm text-earth-500 mb-5">Select all that apply (or skip if none)</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {SKILL_OPTIONS.map(skill => (
                <button key={skill} data-testid={`s2i-skill-${skill.replace(/[^a-zA-Z]/g, '')}`}
                  onClick={() => toggleSkill(skill)}
                  className={`px-3 py-2 rounded-xl text-sm border transition-all ${form.current_skills.includes(skill) ? 'border-primary bg-primary text-white font-semibold' : 'border-earth-200 bg-white text-earth-600 hover:border-earth-300'}`}>
                  {skill}
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm font-semibold text-earth-600 mb-1 block">Other skills (comma-separated)</label>
              <Input data-testid="s2i-custom-skills" value={form.custom_skills} onChange={e => setForm(p => ({ ...p, custom_skills: e.target.value }))}
                placeholder="e.g., AutoCAD, Tableau, SAP" className="border-earth-200 rounded-xl" />
            </div>
          </div>
        )}

        {/* STEP 3: Target Income */}
        {!loading && step === 3 && (
          <div data-testid="s2i-step-3" className="animate-fade-in">
            <h3 className="text-xl font-bold text-earth-700 mb-1">What's your target monthly income?</h3>
            <p className="text-sm text-earth-500 mb-5">Dream big — we'll build the roadmap</p>
            <div className="grid grid-cols-2 gap-3">
              {INCOME_OPTIONS.map(opt => (
                <button key={opt.value} data-testid={`s2i-income-${opt.label.replace(/[^a-zA-Z0-9]/g, '')}`}
                  onClick={() => setForm(p => ({ ...p, target_income: opt.value }))}
                  className={`text-left px-4 py-4 rounded-xl border-2 transition-all ${form.target_income === opt.value ? 'border-primary bg-primary/5 shadow-sm' : 'border-earth-200 hover:border-earth-300 bg-white'}`}>
                  <p className={`text-lg font-bold ${form.target_income === opt.value ? 'text-primary' : 'text-earth-700'}`}>{opt.label}</p>
                  <p className="text-xs text-earth-500">{opt.sub}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 4: Timeline */}
        {!loading && step === 4 && (
          <div data-testid="s2i-step-4" className="animate-fade-in">
            <h3 className="text-xl font-bold text-earth-700 mb-1">What's your timeline?</h3>
            <p className="text-sm text-earth-500 mb-5">How soon do you want to reach your target?</p>
            <div className="grid grid-cols-2 gap-3">
              {TIMELINE_OPTIONS.map(opt => (
                <button key={opt.value} data-testid={`s2i-timeline-${opt.value.replace(/[^a-zA-Z0-9]/g, '')}`}
                  onClick={() => setForm(p => ({ ...p, timeline: opt.value }))}
                  className={`text-left px-4 py-5 rounded-xl border-2 transition-all ${form.timeline === opt.value ? 'border-primary bg-primary/5 shadow-sm' : 'border-earth-200 hover:border-earth-300 bg-white'}`}>
                  <p className={`text-base font-bold ${form.timeline === opt.value ? 'text-primary' : 'text-earth-700'}`}>{opt.label}</p>
                  <p className="text-xs text-earth-500">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 5: Work Preference */}
        {!loading && step === 5 && (
          <div data-testid="s2i-step-5" className="animate-fade-in">
            <h3 className="text-xl font-bold text-earth-700 mb-1">How do you want to earn?</h3>
            <p className="text-sm text-earth-500 mb-5">Pick what suits your lifestyle</p>
            <div className="grid grid-cols-2 gap-3">
              {WORK_OPTIONS.map(opt => (
                <button key={opt.value} data-testid={`s2i-work-${opt.value.replace(/[^a-zA-Z]/g, '')}`}
                  onClick={() => setForm(p => ({ ...p, work_preference: opt.value }))}
                  className={`text-left px-4 py-5 rounded-xl border-2 transition-all ${form.work_preference === opt.value ? 'border-primary bg-primary/5 shadow-sm' : 'border-earth-200 hover:border-earth-300 bg-white'}`}>
                  <opt.icon className={`w-6 h-6 mb-2 ${form.work_preference === opt.value ? 'text-primary' : 'text-earth-400'}`} />
                  <p className={`text-base font-bold ${form.work_preference === opt.value ? 'text-primary' : 'text-earth-700'}`}>{opt.label}</p>
                  <p className="text-xs text-earth-500">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        {!loading && (
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <Button onClick={() => setStep(s => s - 1)} variant="outline" className="h-12 px-6 rounded-xl border-earth-200 text-earth-600">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            <Button
              data-testid={step < TOTAL_STEPS ? 's2i-next-btn' : 's2i-submit-btn'}
              onClick={() => step < TOTAL_STEPS ? setStep(s => s + 1) : handleSubmit()}
              disabled={!canProceed()}
              className="flex-1 bg-primary hover:bg-primary-hover text-white h-12 rounded-xl btn-press disabled:opacity-40">
              {step < TOTAL_STEPS ? (
                <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
              ) : (
                <>Generate My Income Roadmap <Rocket className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Skill2IncomePage;
